// LeadDiscardReasonDialog — обязательный выбор причины перед PATCH лида в DISCARDED (V16 W5).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { rejectionReasonsApi } from '@/api/rejectionReasons';
import { t } from '@/i18n';

/** Пропсы диалога причины отказа при переходе лида в DISCARDED. */
export interface LeadDiscardReasonDialogProps {
  /** Открыт ли диалог. */
  open: boolean;
  /** Закрытие без PATCH (отмена; статус в форме откатывается владельцем). */
  onClose: () => void;
  /** Подтверждение: выбранный id причины; PATCH делает владелец (LeadDetailPage). */
  onConfirm: (rejectionReasonId: string) => void;
  /** Мутация лида в полёте — блокирует повторный submit. */
  pending?: boolean;
}

/**
 * Диалог выбора причины отказа перед переводом лида в DISCARDED.
 * Каталог — GET /api/rejection-reasons; без OK PATCH не уходит.
 */
export function LeadDiscardReasonDialog({
  open,
  onClose,
  onConfirm,
  pending = false,
}: LeadDiscardReasonDialogProps): JSX.Element {
  const [reasonId, setReasonId] = useState('');
  const catalogQuery = useQuery({
    queryKey: ['rejection-reasons'],
    queryFn: () => rejectionReasonsApi.list(),
    enabled: open,
    retry: false,
  });
  const reasons = catalogQuery.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="lead-discard-reason-title">
      <DialogTitle id="lead-discard-reason-title">{t('leads.discardReason.title')}</DialogTitle>
      <DialogContent>
        {catalogQuery.isError ? (
          <Alert severity="error">{t('leads.discardReason.loadError')}</Alert>
        ) : (
          <TextField
            select
            fullWidth
            required
            margin="dense"
            label={t('leads.discardReason.label')}
            value={reasonId}
            onChange={(e) => setReasonId(e.target.value)}
            disabled={catalogQuery.isLoading || pending}
            inputProps={{ 'aria-label': t('leads.discardReason.label') }}
          >
            {reasons.map((reason) => (
              <MenuItem key={reason.id} value={reason.id}>
                {reason.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!reasonId || pending}
          onClick={() => onConfirm(reasonId)}
        >
          {t('leads.discardReason.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
